"use client";

import { useFormContext } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { useI18n } from "@/lib/i18n/hook";
import { useCurrency as useCurrencyContext } from "../contexts/currency-context";
import { CurrencySelect } from "./currency-unit";

export function StepSimulationSettings() {
  const { control } = useFormContext();
  const { t } = useI18n();
  const { symbol } = useCurrencyContext();

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">
        {t("lifePlan.simulationSettings.title")}
      </h2>

      <Card>
        <CardContent className="pt-6 space-y-6">
          <CurrencySelect />

          <FormField
            control={control}
            name="simulationSettings.inflationRate"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>
                    {t("lifePlan.simulationSettings.inflationRate")}
                  </FormLabel>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {t("lifePlan.simulationSettings.inflationRateHelp", {
                            currency: symbol,
                          })}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Slider
                        min={0}
                        max={5}
                        step={0.1}
                        value={[field.value]}
                        onValueChange={(value) => field.onChange(value[0])}
                        className="w-full"
                      />
                    </FormControl>
                    <span className="w-12 text-right">{field.value}%</span>
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="simulationSettings.simulationPeriod"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>
                    {t("lifePlan.simulationSettings.simulationPeriod")}
                  </FormLabel>
                </div>
                <div className="flex items-center gap-2">
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      {...field}
                      onChange={(e) =>
                        field.onChange(Number.parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <span className="text-sm">{t("common.years")}</span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
